import React from "react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronDown, ChevronUp } from "lucide-react";

interface ExpandableSectionProps {
  title: string;
  icon: string;
  isExpanded: boolean;
  onToggle: () => void;
  itemCount: number;
  totalValue: number;
  formatCurrency: (value: number) => string;
  children: React.ReactNode;
  colorScheme?: "primary" | "blue" | "purple" | "green" | "orange" | "muted" | "scheduled";
  nested?: boolean; // New prop
}

const ExpandableSection = ({
  title,
  icon,
  isExpanded,
  onToggle,
  itemCount,
  totalValue,
  formatCurrency,
  children,
  colorScheme = "primary",
  nested = false // Destructure new prop with default value
}: ExpandableSectionProps) => {
  const getTitleColorClasses = () => {
    switch (colorScheme) {
      case "blue":
        return "text-blue-600";
      case "purple":
        return "text-amber-600";
      case "green":
        return "text-green-600";
      case "orange":
        return "text-orange-600";
      case "muted":
        return "text-muted-foreground";
      case "scheduled":
        return "text-blue-700";
      default:
        return "text-primary";
    }
  };

  const getValueColorClasses = () => {
    switch (colorScheme) {
      case "blue":
        return "text-blue-600";
      case "purple":
        return "text-amber-600";
      case "green":
        return "text-green-600";
      case "orange":
        return "text-orange-600";
      case "muted":
        return ""; // Usa cor padrão do texto
      case "scheduled":
        return "text-blue-700";
      default:
        return "text-primary";
    }
  };

  const getContainerClasses = () => {
    if (colorScheme === "scheduled") {
      return "bg-blue-50 border border-dashed border-blue-300 rounded-lg p-3";
    }
    return "";
  };

  const titleColor = getTitleColorClasses();
  const valueColor = getValueColorClasses();
  const containerClasses = getContainerClasses();

  return (
    <Collapsible open={isExpanded} onOpenChange={onToggle}>
      <div className={`space-y-3 ${containerClasses} ${nested ? 'ml-4 pl-2 border-l-2 border-muted-foreground/20' : ''}`}>
        <CollapsibleTrigger asChild>
          <div className="flex justify-between items-center cursor-pointer hover:bg-muted/50 p-2 rounded-lg transition-colors">
            <div className={`flex items-center gap-2 ${titleColor}`}>
              <span className="font-medium">
                {icon} {title}
              </span>
              {isExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </div>
            <span className={`font-bold text-lg ${valueColor}`}>
              {formatCurrency(totalValue)}
            </span>
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent className="space-y-2">
          <div className="ml-4 space-y-3">
            {children}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

export default ExpandableSection;